import {
  createAsyncThunk,
  createSlice,
  PayloadAction,
  SerializedError,
} from '@reduxjs/toolkit'
import {
  loadBigPurchases,
  loadDailyPurchases,
  loadUtilityBills,
  modifyBigPurchase,
  modifyDailyPurchase,
  modifyUtilityBillWithDocument,
  removeBigPurchase,
  removeDailyPurchase,
  removeUtilityBill,
  saveBigPurchase,
  saveDailyPurchase,
  saveUtilityBillWithDocument,
} from '../api/purchase'
import removeFile from '../../Files/api/removeFile'
import {
  BigPurchase,
  CreateBigPurchase,
  CreateDailyPurchase,
  CreateUtilityBillInput,
  DailyPurchase,
  UpdateBigPurchase,
  UpdateDailyPurchase,
  UpdateUtilityBillInput,
  UtilityBill,
} from '../types/purchase'

export const getDailyPurchases = createAsyncThunk(
  'purchaseList/getDailyPurchases',
  async (_, thunkApi) => {
    try {
      return await loadDailyPurchases()
    } catch (e) {
      return thunkApi.rejectWithValue('getDailyPurchasesError')
    }
  },
)

export const addDailyPurchase = createAsyncThunk(
  'purchaseList/addDailyPurchase',
  async (purchase: CreateDailyPurchase, thunkApi) => {
    try {
      return await saveDailyPurchase(purchase)
    } catch (e) {
      return thunkApi.rejectWithValue('addDailyPurchaseError')
    }
  },
)

export const updateDailyPurchase = createAsyncThunk(
  'purchaseList/updateDailyPurchase',
  async (purchase: UpdateDailyPurchase, thunkApi) => {
    try {
      return await modifyDailyPurchase(purchase)
    } catch (e) {
      return thunkApi.rejectWithValue('updateDailyPurchaseError')
    }
  },
)

export const deleteDailyPurchase = createAsyncThunk(
  'purchaseList/deleteDailyPurchase',
  async (id: string, thunkApi) => {
    try {
      return await removeDailyPurchase(id)
    } catch (e) {
      return thunkApi.rejectWithValue('deleteDailyPurchaseError')
    }
  },
)

export const getBigPurchases = createAsyncThunk(
  'purchaseList/getBigPurchases',
  async (_, thunkApi) => {
    try {
      return await loadBigPurchases()
    } catch (e) {
      return thunkApi.rejectWithValue('getBigPurchasesError')
    }
  },
)

export const addBigPurchase = createAsyncThunk(
  'purchaseList/addBigPurchase',
  async (purchase: CreateBigPurchase, thunkApi) => {
    try {
      return await saveBigPurchase(purchase)
    } catch (e) {
      return thunkApi.rejectWithValue('addBigPurchaseError')
    }
  },
)

export const updateBigPurchase = createAsyncThunk(
  'purchaseList/updateBigPurchase',
  async (purchase: UpdateBigPurchase, thunkApi) => {
    try {
      return await modifyBigPurchase(purchase)
    } catch (e) {
      return thunkApi.rejectWithValue('updateBigPurchaseError')
    }
  },
)

export const deleteBigPurchase = createAsyncThunk(
  'purchaseList/deleteBigPurchase',
  async (id: string, thunkApi) => {
    try {
      return await removeBigPurchase(id)
    } catch (e) {
      return thunkApi.rejectWithValue('deleteBigPurchaseError')
    }
  },
)

export const getUtilityBills = createAsyncThunk(
  'purchaseList/getUtilityBills',
  async (_, thunkApi) => {
    try {
      return await loadUtilityBills()
    } catch (e) {
      return thunkApi.rejectWithValue('getUtilityBillsError')
    }
  },
)

export const addUtilityBill = createAsyncThunk(
  'purchaseList/addUtilityBill',
  async (bill: CreateUtilityBillInput, thunkApi) => {
    try {
      return await saveUtilityBillWithDocument(bill)
    } catch (e) {
      return thunkApi.rejectWithValue('addUtilityBillError')
    }
  },
)

export const updateUtilityBill = createAsyncThunk(
  'purchaseList/updateUtilityBill',
  async (bill: UpdateUtilityBillInput, thunkApi) => {
    try {
      return await modifyUtilityBillWithDocument(bill)
    } catch (e) {
      return thunkApi.rejectWithValue('updateUtilityBillError')
    }
  },
)

export const deleteUtilityBill = createAsyncThunk(
  'purchaseList/deleteUtilityBill',
  async (id: string, thunkApi) => {
    try {
      const state = thunkApi.getState() as {
        purchaseList?: { utilityBills?: UtilityBill[] }
      }
      const existing = state.purchaseList?.utilityBills?.find(
        (bill) => bill.id === id,
      )
      const result = await removeUtilityBill(id)
      const fileIds = Array.from(
        new Set(
          [
            ...(existing?.documentFiles || []).map((file) => file.id),
            existing?.documentFileId || '',
          ].filter(Boolean),
        ),
      )

      await Promise.all(
        fileIds.map((fileId) => removeFile(fileId).catch(() => undefined)),
      )

      return result
    } catch (e) {
      return thunkApi.rejectWithValue('deleteUtilityBillError')
    }
  },
)

const sortByCreatedAtDesc = <T extends { createdAt: string }>(items: T[]): T[] => {
  return [...items].sort((a, b) => b.createdAt.localeCompare(a.createdAt))
}

interface PurchaseSliceState {
  dailyPurchases: DailyPurchase[]
  bigPurchases: BigPurchase[]
  utilityBills: UtilityBill[]
  dailyLoading: boolean
  bigLoading: boolean
  utilityLoading: boolean
  error?: SerializedError | string
}

const initialState: PurchaseSliceState = {
  dailyPurchases: [],
  bigPurchases: [],
  utilityBills: [],
  dailyLoading: false,
  bigLoading: false,
  utilityLoading: false,
  error: undefined,
}

const purchaseListSlice = createSlice({
  name: 'purchaseList',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(getDailyPurchases.fulfilled, (
      state,
      action: PayloadAction<DailyPurchase[]>,
    ) => {
      state.dailyPurchases = sortByCreatedAtDesc(action.payload)
      state.dailyLoading = false
      state.error = undefined
    })
    builder.addCase(getDailyPurchases.pending, (state) => {
      state.dailyLoading = true
      state.error = undefined
    })
    builder.addCase(getDailyPurchases.rejected, (state, action) => {
      state.dailyLoading = false
      state.error = action.error
    })
    builder.addCase(addDailyPurchase.fulfilled, (
      state,
      action: PayloadAction<DailyPurchase>,
    ) => {
      state.dailyPurchases = sortByCreatedAtDesc([
        action.payload,
        ...state.dailyPurchases,
      ])
      state.dailyLoading = false
      state.error = undefined
    })
    builder.addCase(addDailyPurchase.pending, (state) => {
      state.dailyLoading = true
      state.error = undefined
    })
    builder.addCase(addDailyPurchase.rejected, (state, action) => {
      state.dailyLoading = false
      state.error = action.error
    })
    builder.addCase(updateDailyPurchase.fulfilled, (
      state,
      action: PayloadAction<DailyPurchase>,
    ) => {
      state.dailyPurchases = sortByCreatedAtDesc(
        state.dailyPurchases.map((purchase) =>
          purchase.id === action.payload.id ? action.payload : purchase,
        ),
      )
      state.dailyLoading = false
      state.error = undefined
    })
    builder.addCase(updateDailyPurchase.pending, (state) => {
      state.dailyLoading = true
      state.error = undefined
    })
    builder.addCase(updateDailyPurchase.rejected, (state, action) => {
      state.dailyLoading = false
      state.error = action.error
    })
    builder.addCase(deleteDailyPurchase.fulfilled, (
      state,
      action: PayloadAction<string>,
    ) => {
      state.dailyPurchases = state.dailyPurchases.filter(
        (purchase) => purchase.id !== action.payload,
      )
      state.dailyLoading = false
      state.error = undefined
    })
    builder.addCase(deleteDailyPurchase.pending, (state) => {
      state.dailyLoading = true
      state.error = undefined
    })
    builder.addCase(deleteDailyPurchase.rejected, (state, action) => {
      state.dailyLoading = false
      state.error = action.error
    })
    builder.addCase(getBigPurchases.fulfilled, (
      state,
      action: PayloadAction<BigPurchase[]>,
    ) => {
      state.bigPurchases = sortByCreatedAtDesc(action.payload)
      state.bigLoading = false
      state.error = undefined
    })
    builder.addCase(getBigPurchases.pending, (state) => {
      state.bigLoading = true
      state.error = undefined
    })
    builder.addCase(getBigPurchases.rejected, (state, action) => {
      state.bigLoading = false
      state.error = action.error
    })
    builder.addCase(addBigPurchase.fulfilled, (
      state,
      action: PayloadAction<BigPurchase>,
    ) => {
      state.bigPurchases = sortByCreatedAtDesc([action.payload, ...state.bigPurchases])
      state.bigLoading = false
      state.error = undefined
    })
    builder.addCase(addBigPurchase.pending, (state) => {
      state.bigLoading = true
      state.error = undefined
    })
    builder.addCase(addBigPurchase.rejected, (state, action) => {
      state.bigLoading = false
      state.error = action.error
    })
    builder.addCase(updateBigPurchase.fulfilled, (
      state,
      action: PayloadAction<BigPurchase>,
    ) => {
      state.bigPurchases = sortByCreatedAtDesc(
        state.bigPurchases.map((purchase) =>
          purchase.id === action.payload.id ? action.payload : purchase,
        ),
      )
      state.bigLoading = false
      state.error = undefined
    })
    builder.addCase(updateBigPurchase.pending, (state) => {
      state.bigLoading = true
      state.error = undefined
    })
    builder.addCase(updateBigPurchase.rejected, (state, action) => {
      state.bigLoading = false
      state.error = action.error
    })
    builder.addCase(deleteBigPurchase.fulfilled, (
      state,
      action: PayloadAction<string>,
    ) => {
      state.bigPurchases = state.bigPurchases.filter(
        (purchase) => purchase.id !== action.payload,
      )
      state.bigLoading = false
      state.error = undefined
    })
    builder.addCase(deleteBigPurchase.pending, (state) => {
      state.bigLoading = true
      state.error = undefined
    })
    builder.addCase(deleteBigPurchase.rejected, (state, action) => {
      state.bigLoading = false
      state.error = action.error
    })
    builder.addCase(getUtilityBills.fulfilled, (
      state,
      action: PayloadAction<UtilityBill[]>,
    ) => {
      state.utilityBills = sortByCreatedAtDesc(action.payload)
      state.utilityLoading = false
      state.error = undefined
    })
    builder.addCase(getUtilityBills.pending, (state) => {
      state.utilityLoading = true
      state.error = undefined
    })
    builder.addCase(getUtilityBills.rejected, (state, action) => {
      state.utilityLoading = false
      state.error = action.error
    })
    builder.addCase(addUtilityBill.fulfilled, (
      state,
      action: PayloadAction<UtilityBill>,
    ) => {
      state.utilityBills = sortByCreatedAtDesc([
        action.payload,
        ...state.utilityBills,
      ])
      state.utilityLoading = false
      state.error = undefined
    })
    builder.addCase(addUtilityBill.pending, (state) => {
      state.utilityLoading = true
      state.error = undefined
    })
    builder.addCase(addUtilityBill.rejected, (state, action) => {
      state.utilityLoading = false
      state.error = action.error
    })
    builder.addCase(updateUtilityBill.fulfilled, (
      state,
      action: PayloadAction<UtilityBill>,
    ) => {
      state.utilityBills = sortByCreatedAtDesc(
        state.utilityBills.map((bill) =>
          bill.id === action.payload.id ? action.payload : bill,
        ),
      )
      state.utilityLoading = false
      state.error = undefined
    })
    builder.addCase(updateUtilityBill.pending, (state) => {
      state.utilityLoading = true
      state.error = undefined
    })
    builder.addCase(updateUtilityBill.rejected, (state, action) => {
      state.utilityLoading = false
      state.error = action.error
    })
    builder.addCase(deleteUtilityBill.fulfilled, (
      state,
      action: PayloadAction<string>,
    ) => {
      state.utilityBills = state.utilityBills.filter(
        (bill) => bill.id !== action.payload,
      )
      state.utilityLoading = false
      state.error = undefined
    })
    builder.addCase(deleteUtilityBill.pending, (state) => {
      state.utilityLoading = true
      state.error = undefined
    })
    builder.addCase(deleteUtilityBill.rejected, (state, action) => {
      state.utilityLoading = false
      state.error = action.error
    })
  },
})

export default purchaseListSlice.reducer
